<template>
  <div>
    <div class="left-panel-c">
      <div class="panel-card">
        <div class="panel-card-header">
          <!-- 农事配置标题区域 -->
          <div class="config-header">
            <h2 class="config-title">农事配置</h2>
            <div class="config-buttons">
              <button class="config-btn" @click="openAssignDialog">
                配置农事任务
              </button>
              <button class="config-btn" @click="openGrowUpDialog">
                配置生长时期
              </button>
            </div>
          </div>

          <!-- 本月农事任务统计 -->
          <div class="task-stats">
            <h3 class="section-title">本月农事任务</h3>
            <div class="stats-cards">
              <div class="stat-card">
                <div class="stat-number">
                  {{ taskStats.waitingTaskCount || 0 }}
                </div>
                <div class="stat-desc">待执行任务数(个)</div>
              </div>
              <div class="stat-card">
                <div class="stat-number">
                  {{ taskStats.completedTaskCount || 0 }}
                </div>
                <div class="stat-desc">已完成任务数(个)</div>
              </div>
              <div class="stat-card">
                <div class="stat-number">
                  {{ taskStats.delayedTaskCount || 0 }}
                </div>
                <div class="stat-desc">已延期任务数(个)</div>
              </div>
            </div>
          </div>

          <!-- 本期生长时期分布 -->
          <div class="growth-distribution">
            <div class="distribution-header">
              <h3 class="section-title">本期生长时期分布</h3>
              <div class="variety-selector">
                <span>{{
                  varietyOptions.length ? varietyOptions[0].label : "暂无数据"
                }}</span>
              </div>
            </div>
            <div class="growth-stages">
              <div
                class="stage-card"
                :class="getStageCardClass(item)"
                v-for="item in cycleDetailOptions"
                :key="item.basicStageId"
              >
                <img
                  :src="getBackgroundImage(item)"
                  class="stage-background"
                  :alt="item.basicStageName"
                />
                <div class="stage-content">
                  <div class="stage-name" :style="getStageNameStyle(item)">
                    <img
                      v-if="isInGrowthPeriod(item)"
                      :src="getStageNameBackground(item)"
                      class="stage-name-bg"
                      :alt="item.basicStageName"
                    />
                    <span class="stage-name-text">{{
                      item.basicStageName
                    }}</span>
                  </div>
                  <div class="stage-date" :style="getStageDateStyle(item)">
                    <span class="date-bullet" :style="getDateBulletStyle(item)"
                      >•</span
                    >{{ formatDate(item.startDate) }}-{{
                      formatDate(item.endDate)
                    }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- 农事任务详情 -->
      <div class="panel-card">
        <h3 class="section-title1">农事任务详情</h3>
        <div class="task-detail-table">
          <table>
            <thead>
              <tr>
                <th>执行周期</th>
                <th>执行内容</th>
                <th>执行人</th>
                <th>状态</th>
                <th>执行要求</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="task in taskInfo" :key="task.id">
                <td>
                  {{ formatDate(task.execStartDate) }}-{{
                    formatDate(task.execEndDate)
                  }}
                </td>
                <td>{{ task.actions }}</td>
                <td>{{ task.executorNickName }}</td>
                <td>
                  <span
                    class="status-btn"
                    :class="getStatusClass(task.execStatus)"
                  >
                    {{ getStatusText(task.execStatus) }}
                  </span>
                </td>
                <td>
                  <el-button
                    v-if="
                      task.execStatus !== 'COMPLETED' &&
                      getAllMenuCodes.includes('fCxBreY1')
                    "
                    type="text"
                    style="color: #2680dd"
                    @click="handleExecute(task)"
                    >执行</el-button
                  >
                  <el-button
                    type="text"
                    style="color: #04ff9f"
                    @click="handleView(task)"
                    >查看</el-button
                  >
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
    <!-- 任务执行弹窗 -->
    <TaskExecutionDialog
      :visible.sync="viewDialogVisible"
      :taskData="selectedTask"
      :mode="dialogMode"
      @success="handleTaskSuccess"
    />

    <!-- 配置农事任务弹窗 -->
    <assign-dialog
      :visible.sync="assignDialogVisible"
      :is-edit="false"
      :edit-data="{}"
      :greenhouse-options="greenhouseOptions"
      :variety-options="varietyOptions"
      :task-category-options="taskCategoryOptions"
      :executor-cascader-options="executorCascaderOptions"
      :greenhouse-id="greenhouseId"
      @success="handleAssignSuccess"
    />

    <!-- 配置生长时期弹窗 -->
    <grow-up-dialog
      :visible.sync="growUpDialogVisible"
      :is-edit="false"
      :edit-data="{}"
      :greenhouse-id="greenhouseId"
      @success="handleGrowUpSuccess"
    />
  </div>
</template>

<script>
import axios from "axios";
import { baseUrl } from "@/axios/baseUrl";
import { isCurrentTimeInRange, formatDateToMMDD } from "@/utils/dateUtils";
import TaskExecutionDialog from "../TaskExecutionDialog.vue";
import assignDialog from "../assignDialog.vue";
import growUpDialog from "../GrowUpDialog.vue";
import { mapGetters } from "vuex";
export default {
  name: "LeftPanel",
  components: {
    TaskExecutionDialog,
    assignDialog,
    growUpDialog,
  },
  props: {
    greenhouseId: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      taskStats: {
        waitingTaskCount: 0,
        completedTaskCount: 0,
        delayedTaskCount: 0,
      },
      varietyOptions: [],
      varietyId: "",
      cycleDetailOptions: [],
      timeUpdateInterval: null,
      taskInfo: [],
      currentStageInfo: {
        name: "",
        inPeriod: false,
        index: 0,
      },
      viewDialogVisible: false,
      selectedTask: {},
      dialogMode: "view", // 新增：用于控制弹窗模式
      // 新增弹窗状态
      assignDialogVisible: false,
      growUpDialogVisible: false,
      // 新增选项数据
      greenhouseOptions: [],
      taskCategoryOptions: [],
      executorCascaderOptions: [],
    };
  },
  computed: {
    // 计算当前时间，用于触发重新渲染
    currentTime() {
      return new Date().getTime();
    },
    ...mapGetters("user", ["getAllMenuCodes"]),
  },
  watch: {
    greenhouseId: {
      handler(newVal) {
        if (newVal) {
          this.getTaskStatistics();
          this.getTaskInfo();
          setTimeout(() => {
            this.fetchVarietyOptions(newVal);
          }, 355);
        }
      },
      immediate: true,
    },
  },
  methods: {
    async getTaskStatistics() {
      try {
        if (!this.greenhouseId) {
          return;
        }

        const response = await axios.post(
          `${baseUrl}/api/digital-farming/task/allot-record/statistics-by-greenhouse-id/${this.greenhouseId}`
        );
        if (response.data && response.code === 200) {
          this.taskStats = {
            waitingTaskCount: response.data.waitingTaskCount || 0,
            completedTaskCount: response.data.completedTaskCount || 0,
            delayedTaskCount: response.data.delayedTaskCount || 0,
          };
        } else {
          console.error("获取农事任务统计数据失败:", response.data);
        }
      } catch (error) {
        console.error("调用农事任务统计接口出错:", error);
      }
    },
    async fetchVarietyOptions(greenhouseId) {
      try {
        const res = await axios.post(
          baseUrl +
            "/api/digital-farming/growth-cycle/growth-period-distribution",
          {
            enabled: 1,
            greenhouseId: greenhouseId,
          }
        );
        this.varietyOptions = res.data.map((item) => ({
          label: item.varietyName,
          value: item.varietyId,
          details: item.details,
        }));

        this.cycleDetailOptions = [];
        this.varietyId = "";
        if (this.varietyOptions.length > 0) {
          this.varietyId = this.varietyOptions[0].value;
          this.cycleDetailOptions = this.varietyOptions[0].details;
          // 更新当前生长时期信息
        }
        this.updateCurrentStageInfo();
      } catch (error) {
        console.error("获取品种选项失败:", error);
      }
    },
    getCycleDetailOptions() {
      this.cycleDetailOptions = this.varietyOptions.find(
        (item) => item.value === this.varietyId
      ).details;
      // 更新当前生长时期信息
      this.updateCurrentStageInfo();
    },
    // 获取农事任务详情
    async getTaskInfo() {
      const res = await axios.post(
        baseUrl +
          "/api/digital-farming/task/allot-record/list-latest/" +
          this.greenhouseId
      );
      this.taskInfo = [];

      if (res.code === 200) {
        this.taskInfo = res.data;
        console.log("详情", this.taskInfo);
      }
    },
    // 获取状态样式类
    getStatusClass(status) {
      switch (status) {
        case "WAITING":
          return "status-pending";
        case "DELAYED":
          return "status-delayed";
        case "COMPLETED":
          return "status-completed";
        default:
          return "status-pending";
      }
    },
    // 获取状态文本
    getStatusText(status) {
      switch (status) {
        case "WAITING":
          return "待执行";
        case "DELAYED":
          return "已延期";
        case "COMPLETED":
          return "已完成";
        default:
          return "待执行";
      }
    },

    formatDate(dateString) {
      return formatDateToMMDD(dateString);
    },
    getStageCardClass(item) {
      const isInGrowthPeriod = isCurrentTimeInRange(
        item.startDate,
        item.endDate
      );
      return {
        seedling: !isInGrowthPeriod,
        "growth-period": isInGrowthPeriod,
      };
    },
    getBackgroundImage(item) {
      const isInGrowthPeriod = isCurrentTimeInRange(
        item.startDate,
        item.endDate
      );
      const stageIndex = this.cycleDetailOptions.findIndex(
        (stage) => stage.basicStageId === item.basicStageId
      );

      // 计算循环索引，当超过8个时循环显示
      const getImageIndex = (index) => {
        return index % 8;
      };

      try {
        if (!isInGrowthPeriod) {
          // 如果在生长时期内，使用s开头的图片
          const sIndex = getImageIndex(stageIndex);
          return require(`@/assets/img/stage/s${sIndex}.png`);
        } else {
          // 如果不在生长时期内，使用a开头的图片
          const aIndex = getImageIndex(stageIndex);
          return require(`@/assets/img/stage/a${aIndex}.png`);
        }
      } catch (error) {
        // 如果图片加载失败，返回默认图片
        return require("@/assets/img/stage/a0.png");
      }
    },
    getStageNameStyle(item) {
      const stageIndex = this.cycleDetailOptions.findIndex(
        (stage) => stage.basicStageId === item.basicStageId
      );
      const isInGrowthPeriod = isCurrentTimeInRange(
        item.startDate,
        item.endDate
      );

      // 计算循环索引
      const getImageIndex = (index) => {
        return index % 8;
      };

      const imageIndex = getImageIndex(stageIndex);

      // 定义不同时期的背景色
      const stageColors = [
        "rgba(212, 249, 215, 0.40)", // 0
        "rgba(140, 230, 143, 0.40)", // 1
        "rgba(0, 218, 167, 0.40)", // 2
        "rgba(0, 150, 200, 0.40)", // 3
        "rgba(218, 58, 0, 0.40)", // 4
        "rgba(218, 156, 0, 0.40)", // 5
        "rgba(218, 116, 0, 0.40)", // 6
        "rgba(0, 127, 218, 0.40)", // 7
      ];

      const baseColor = stageColors[imageIndex];

      if (isInGrowthPeriod) {
        // 如果在生长期内，使用背景图，不设置背景色
        return {
          textAlign: "center",
          width: "100%",
          position: "relative",
          marginBottom: "2px",
        };
      } else {
        // 如果不在生长期内，使用半透明背景色
        return {
          backgroundColor: baseColor,
          textAlign: "center",
          width: "100%",
          position: "relative",
          marginBottom: "2px",
        };
      }
    },
    isInGrowthPeriod(item) {
      return isCurrentTimeInRange(item.startDate, item.endDate);
    },
    getStageNameBackground(item) {
      const stageIndex = this.cycleDetailOptions.findIndex(
        (stage) => stage.basicStageId === item.basicStageId
      );

      // 计算循环索引
      const getImageIndex = (index) => {
        return index % 8;
      };

      const imageIndex = getImageIndex(stageIndex);

      try {
        return require(`@/assets/img/stage/b${imageIndex}.png`);
      } catch (error) {
        // 如果图片加载失败，返回默认图片
        return require("@/assets/img/stage/b0.png");
      }
    },
    getStageDateStyle(item) {
      const stageIndex = this.cycleDetailOptions.findIndex(
        (stage) => stage.basicStageId === item.basicStageId
      );

      // 计算循环索引
      const getImageIndex = (index) => {
        return index % 8;
      };

      const imageIndex = getImageIndex(stageIndex);

      // 定义不同时期的颜色（与日期颜色相同）
      const stageColors = [
        "rgb(212, 249, 215)", // 0 - 浅绿色
        "rgb(140, 230, 143)", // 1 - 绿色
        "rgb(0, 218, 167)", // 2 - 青绿色
        "rgb(0, 150, 200)", // 3 - 蓝色
        "rgb(218, 58, 0)", // 4 - 红色
        "rgb(218, 156, 0)", // 5 - 橙色
        "rgb(218, 116, 0)", // 6 - 橙红色
        "rgb(0, 127, 218)", // 7 - 蓝色
      ];

      const textColor = stageColors[imageIndex];

      return {
        color: textColor,
        fontSize: "10px",
        fontWeight: "400",
        textShadow: "0px 0px 4px rgba(0, 0, 0, 0.8)",
      };
    },
    getDateBulletStyle(item) {
      const stageIndex = this.cycleDetailOptions.findIndex(
        (stage) => stage.basicStageId === item.basicStageId
      );

      // 计算循环索引
      const getImageIndex = (index) => {
        return index % 8;
      };

      const imageIndex = getImageIndex(stageIndex);

      // 定义不同时期的颜色（与日期颜色相同）
      const stageColors = [
        "rgb(212, 249, 215)", // 0 - 浅绿色
        "rgb(140, 230, 143)", // 1 - 绿色
        "rgb(0, 218, 167)", // 2 - 青绿色
        "rgb(0, 150, 200)", // 3 - 蓝色
        "rgb(218, 58, 0)", // 4 - 红色
        "rgb(218, 156, 0)", // 5 - 橙色
        "rgb(218, 116, 0)", // 6 - 橙红色
        "rgb(0, 127, 218)", // 7 - 蓝色
      ];

      const bulletColor = stageColors[imageIndex];

      return {
        color: bulletColor,
        marginRight: "4px",
        fontWeight: "bold",
      };
    },

    // 计算当前时间所在的生长时期
    updateCurrentStageInfo() {
      console.log("生长时期", this.cycleDetailOptions);

      if (this.cycleDetailOptions.length > 0) {
        // 找到当前时间所在的生长时期
        for (let i = 0; i < this.cycleDetailOptions.length; i++) {
          const stage = this.cycleDetailOptions[i];
          if (isCurrentTimeInRange(stage.startDate, stage.endDate)) {
            this.currentStageInfo = {
              name: stage.basicStageName,
              inPeriod: true,
              varietyId: this.varietyId,
              index: i,
              allCycleDetailOptions: this.cycleDetailOptions,
            };
            // 发射事件给父组件
            this.$emit("current-stage-updated", this.currentStageInfo);
            return;
          }
        }
        // 如果没有找到当前时间所在的时期，使用第一个时期
        this.currentStageInfo = {
          name: "暂无",
          inPeriod: false,
          varietyId: this.varietyId,
          index: 0,
          allCycleDetailOptions: this.cycleDetailOptions,
        };
        // 发射事件给父组件
        this.$emit("current-stage-updated", this.currentStageInfo);
      } else {
        this.currentStageInfo = {
          name: "暂无",
          inPeriod: false,
          varietyId: 0,
          index: 0,
          allCycleDetailOptions: this.cycleDetailOptions,
        };
        this.$emit("current-stage-updated", this.currentStageInfo);
      }
    },
    handleView(task) {
      this.selectedTask = task;
      console.log("查看详情", task);
      this.dialogMode = "view"; // 设置弹窗模式为查看
      this.viewDialogVisible = true;
    },
    handleExecute(task) {
      this.selectedTask = task;
      this.dialogMode = "execute"; // 设置弹窗模式为执行
      this.viewDialogVisible = true;
    },
    handleTaskSuccess() {
      this.viewDialogVisible = false;
      this.selectedTask = {};
      this.getTaskInfo(); // 刷新任务列表
    },
    openAssignDialog() {
      this.assignDialogVisible = true;
    },
    openGrowUpDialog() {
      this.growUpDialogVisible = true;
    },
    handleAssignSuccess() {
      this.assignDialogVisible = false;
      this.getTaskInfo(); // 刷新任务列表
    },
    handleGrowUpSuccess() {
      this.growUpDialogVisible = false;
      this.getTaskInfo(); // 刷新任务列表
    },
    // 获取选项数据
    async fetchOptionsData() {
      try {
        // 获取大棚选项
        const greenhouseRes = await axios.post(
          baseUrl + "/api/basic/greenhouse/selection"
        );
        if (greenhouseRes.code === 200) {
          this.greenhouseOptions = greenhouseRes.data.map((item) => ({
            label: item.serialNumber,
            value: item.id,
          }));
        }

        // 获取任务分类选项
        const taskCategoryRes = await axios.get(
          baseUrl + "/api/farming/task-config/selectionV2"
        );
        if (taskCategoryRes.code === 200) {
          this.taskCategoryOptions = taskCategoryRes.data.map((item) => ({
            label: item.categoryName,
            categoryId: item.categoryId,
            categoryActionsList: item.categoryActionsList,
          }));
        }
        console.log("任务分类", this.taskCategoryOptions);

        const executorRes = await axios.post(baseUrl + "/api/system/users");
        if (executorRes.code === 200) {
          // 过滤出角色为农技员的用户
          const agriTechUsers = executorRes.data.filter(
            (user) =>
              user.roleCodes &&
              user.roleCodes.split(",").includes("greenhouse_admin")
          );

          // 构建级联选择器数据
          this.executorCascaderOptions = agriTechUsers.map((item) => ({
            id: item.id,
            name: `${item.nickName || item.realName || ""} (${item.username})`,
            username: item.username,
            nickName: item.nickName || item.realName,
          }));
        } else {
          console.error("获取用户列表失败:", res.message);
        }
      } catch (error) {
        console.error("获取选项数据失败:", error);
      }
    },
  },
  mounted() {
    // 每分钟更新一次，确保时间变化时背景图能够及时更新
    this.timeUpdateInterval = setInterval(() => {
      this.$forceUpdate();
      // 同时更新当前生长时期信息
      this.updateCurrentStageInfo();
    }, 60000); // 60秒更新一次

    // 获取必要的选项数据
    this.fetchOptionsData();
  },
  beforeDestroy() {
    // 清理定时器
    if (this.timeUpdateInterval) {
      clearInterval(this.timeUpdateInterval);
    }
  },
};
</script>

<style lang="less" scoped>
.left-panel-c {
  height: calc(100vh - 155px);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.panel-card {
  padding: 10px;

  .section-title {
    width: 100%;
    height: 50px;
    line-height: 50px;
    font-weight: bold;
    font-size: 20px;
    color: #ffffff;
    text-indent: 10px;
    text-shadow: 0px 0px 8px #1286ff;
    border-bottom: 1px solid rgba(255, 255, 255, 0.2);
    margin-bottom: 22px;
  }

  .section-title1 {
    width: 100%;
    height: 30px;
    line-height: 30px;
    font-weight: bold;
    font-size: 24px;
    color: #ffffff;
    text-shadow: 0px 0px 11px #6cc4ff;
    margin-bottom: 9px;
  }

  .task-detail-table {
    background: rgba(0, 11, 47, 0.3);
    border-radius: 8px;
    margin-top: 10px;
    min-height: 188px;
    height: 32vh;
    overflow-y: auto;

    table {
      width: 100%;
      border-collapse: collapse;
      color: #ffffff;

      thead {
        th {
          background: rgba(0, 124, 255, 0.2);
          padding: 12px 8px;
          text-align: center;
          font-weight: bold;
          font-size: 14px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.2);
        }
      }

      tbody {
        tr {
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);

          &:hover {
            background: rgba(255, 255, 255, 0.05);
          }

          td {
            padding: 10px 8px;
            text-align: center;
            font-size: 12px;
            vertical-align: middle;
          }
        }
      }
    }

    .status-btn {
      display: inline-block;
      padding: 4px 12px;
      border-radius: 4px;
      font-size: 12px;
      font-weight: 500;
      min-width: 60px;

      &.status-pending {
        background: rgba(59, 130, 246, 0.8);
        color: #ffffff;
      }

      &.status-delayed {
        background: rgba(251, 146, 60, 0.8);
        color: #ffffff;
      }

      &.status-completed {
        background: rgba(34, 197, 94, 0.8);
        color: #ffffff;
      }
    }

    .view-btn {
      color: #60a5fa;
      cursor: pointer;
      font-size: 12px;
      text-decoration: underline;

      &:hover {
        color: #3b82f6;
      }
    }

    /* 自定义滚动条样式 */
    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      background: rgba(0, 11, 47, 0.5);
      border-radius: 3px;
    }

    &::-webkit-scrollbar-thumb {
      background: rgba(59, 130, 246, 0.6);
      border-radius: 3px;

      &:hover {
        background: rgba(59, 130, 246, 0.8);
      }
    }
  }

  .panel-card-header {
    .config-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 9px;

      .config-title {
        font-weight: bold;
        font-size: 24px;
        color: #ffffff;
        text-shadow: 0px 0px 11px #6cc4ff;
        text-align: left;
        font-style: normal;
        text-transform: none;
      }

      .config-buttons {
        display: flex;
        gap: 12px;

        .config-btn {
          background: #3b82f6;
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 6px;
          font-size: 14px;
          cursor: pointer;
          transition: background-color 0.3s;

          &:hover {
            background: #2563eb;
          }
        }
      }
    }

    .task-stats {
      background: url("../../../../assets/img/stage/l_border-1.png") no-repeat
        center center;
      background-size: 100% 100%;
      width: 100%;
      height: 166px;

      .stats-cards {
        display: flex;
        justify-content: center;
        gap: 10px;
        padding: 0 10px;

        .stat-card {
          flex: 1;
          background: rgba(17, 104, 177, 0.5);
          text-align: center;

          .stat-number {
            font-size: 30px;
            line-height: 39px;

            font-weight: 800;
            background: linear-gradient(180deg, #ffffff 0%, #95d2f0 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
            margin-bottom: 9px;
          }

          .stat-desc {
            background: rgba(7, 44, 75, 0.5);

            height: 38px;
            font-size: 14px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: 400;
            color: #ffffff;
          }
        }
      }
    }

    .growth-distribution {
      background: url("../../../../assets/img/stage/l_border-2.png") no-repeat
        center center;
      background-size: 100% 100%;
      width: 100%;
      height: 258px;
      margin-top: 22px;

      .distribution-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 10px;
        border-bottom: 1px solid rgba(255, 255, 255, 0.2);

        .section-title {
          width: 65%;
          margin-bottom: 0;
          border: none;
        }

        .variety-selector {
          display: flex;
          align-items: center;
          gap: 8px;
          background: rgba(59, 130, 246, 0.2);
          padding: 6px 12px;
          border-radius: 6px;
          margin-right: 10px;
          cursor: pointer;
          font-size: 14px;
          color: #fff;
        }
      }

      .growth-stages {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 12px;
        /* 限制最大高度，当内容超出时显示滚动条 */
        max-height: calc(100% - 60px);
        overflow-y: auto;
        padding-right: 6px; /* 给滚动条留出空间，避免遮挡内容 */

        .stage-card {
          text-align: center;
          transition: transform 0.2s;
          width: 100%;
          height: 95px;
          border-radius: 6px;
          box-sizing: border-box;
          position: relative;

          &:hover {
            transform: translateY(-2px);
          }

          .stage-background {
            position: absolute;
            bottom: 0;
            left: 50%;
            transform: translateX(-50%);
            width: 73px;
            height: 67px;
            object-fit: contain;
            z-index: 1;
          }

          .stage-content {
            position: relative;
            z-index: 2;
            height: 100%;
            display: flex;
            flex-direction: column;
            justify-content: flex-start;
            align-items: center;
            padding-top: 4px;
          }

          .stage-name {
            font-family: YouSheBiaoTiHei, YouSheBiaoTiHei;
            font-weight: 400;
            font-size: 20px;
            color: rgba(255, 255, 255, 0.6);
            text-align: center;
            display: inline-block;
            text-align: center;
            margin-bottom: 2px;
            position: relative;

            .stage-name-bg {
              position: absolute;
              top: 0;
              left: 0;
              width: 100%;
              height: 34px;
              object-fit: contain;
              z-index: 1;
            }

            .stage-name-text {
              position: relative;
              z-index: 2;
              display: block;
              width: 100%;
              height: 34px;
              display: flex;
              align-items: center;
              justify-content: center;
            }
          }

          .stage-date {
            font-weight: 500;
            font-size: 18px;

            .date-bullet {
              margin-right: 4px;
              font-weight: bold;
            }
          }
        }
      }
    }
  }
}
</style>
